import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiX,
  FiUser,
  FiMail,
  FiLock,
  FiPhone,
  FiBriefcase,
  FiEye,
  FiEyeOff,
  FiMapPin,
  FiSettings,
  FiNavigation,
  FiPlus,
  FiTrash2,
  FiStar,
  FiLoader,
  FiCpu,
  FiSend,
  FiPackage,
  FiImage,
  FiTruck,
  FiLayers,
  FiUpload,
} from 'react-icons/fi';
import API from 'shared/api';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import { getAdminUrl, empresaDominio } from 'shared/config';

const BP = 768;
const COL = {
  pri: '#0a0e27',
  pri2: '#2952CC',
  acc: '#6382eb',
  acc2: '#4f6ef2',
  bg: '#f7f8fc',
  white: '#FFFFFF',
  text: '#202124',
  muted: '#5e6570',
  border: '#e8eaef',
  green: '#10b981',
  greenBg: '#ecfdf5',
  red: '#ea4335',
  redBg: '#fef2f2',
  amber: '#f59e0b',
  amberBg: '#fffbeb',
};

function fmtMd(t) {
  if (!t) return '';
  return t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/gs, '<em>$1</em>')
    .replace(
      /`(.+?)`/g,
      '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:12px">$1</code>'
    )
    .replace(/\n/g, '<br/>');
}

const PAISES = [
  { codigo: '+58', pais: 'Venezuela', bandera: '\u{1F1FB}\u{1F1EA}' },
  { codigo: '+57', pais: 'Colombia', bandera: '\u{1F1E8}\u{1F1F4}' },
  { codigo: '+1', pais: 'EEUU/Canada', bandera: '\u{1F1FA}\u{1F1F8}' },
  { codigo: '+52', pais: 'Mexico', bandera: '\u{1F1F2}\u{1F1FD}' },
  { codigo: '+54', pais: 'Argentina', bandera: '\u{1F1E6}\u{1F1F7}' },
  { codigo: '+55', pais: 'Brasil', bandera: '\u{1F1E7}\u{1F1F7}' },
  { codigo: '+56', pais: 'Chile', bandera: '\u{1F1E8}\u{1F1F1}' },
  { codigo: '+51', pais: 'Peru', bandera: '\u{1F1F5}\u{1F1EA}' },
  { codigo: '+34', pais: 'España', bandera: '\u{1F1EA}\u{1F1F8}' },
  { codigo: '+593', pais: 'Ecuador', bandera: '\u{1F1EA}\u{1F1E8}' },
];

const PASOS = [
  { n: 1, label: 'Plan', icon: FiPackage },
  { n: 2, label: 'Empresa', icon: FiBriefcase },
  { n: 3, label: 'Admin', icon: FiUser },
  { n: 4, label: 'Sede', icon: FiMapPin },
  { n: 5, label: 'Operación', icon: FiSettings },
  { n: 6, label: 'Bot IA', icon: FiCpu },
];

const MONEDAS_PRED = [
  { nombre: 'Dolar Estadounidense', codigo: 'USD', simbolo: '$' },
  { nombre: 'Bolivar', codigo: 'VES', simbolo: 'Bs' },
  { nombre: 'Peso Colombiano', codigo: 'COP', simbolo: '$' },
  { nombre: 'Peso Argentino', codigo: 'ARS', simbolo: '$' },
  { nombre: 'Peso Mexicano', codigo: 'MXN', simbolo: '$' },
  { nombre: 'Real Brasileno', codigo: 'BRL', simbolo: 'R$' },
  { nombre: 'Peso Chileno', codigo: 'CLP', simbolo: '$' },
  { nombre: 'Sol Peruano', codigo: 'PEN', simbolo: 'S/' },
  { nombre: 'Euro', codigo: 'EUR', simbolo: '\u20AC' },
];

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

function RegistroEmpresa() {
  const nav = useNavigate();
  const width = useWindowWidth();
  const mobile = width < BP;
  const [paso, setPaso] = useState(1);

  const [empresa, setEmpresa] = useState({
    nombre: '',
    nombre_representante: '',
    ci_rif: '',
    telefono: '',
    codigo_pais: '+58',
  });
  const [admin, setAdmin] = useState({
    nombre: '',
    cedula: '',
    usuario: '',
    correo: '',
    clave: '',
    confirmar_clave: '',
  });
  const [sede, setSede] = useState({ nombre: '', direccion: '', lat: 10.5, lng: -66.9 });
  const [config, setConfig] = useState({
    inventario_tipo: 'interno',
    tipos_delivery: ['retiro'],
    modo: 'ambos',
    nombre_app: '',
    color_primario: '#0B1A4A',
    color_secundario: '#2952CC',
    tipo_empresa: 'general',
  });
  const [monedas, setMonedas] = useState([
    {
      nombre: 'Dolar Estadounidense',
      codigo: 'USD',
      simbolo: '$',
      tasa_cambio: 1,
      es_principal: true,
    },
  ]);
  const [monedaCustom, setMonedaCustom] = useState({ nombre: '', codigo: '', simbolo: '' });
  const [plan, setPlan] = useState({ tipo: 'interno', integracion_externa_url: '' });
  const [logo, setLogo] = useState({ base64: null, preview: null });

  const [slugVal, setSlugVal] = useState({ cargando: false, disponible: null, sugerido: '' });
  const [cedulaVal, setCedulaVal] = useState({ cargando: false, disponible: null });
  const [usuarioVal, setUsuarioVal] = useState({ cargando: false, disponible: null });
  const [correoVal, setCorreoVal] = useState({ cargando: false, disponible: null });

  const [mostrarClave, setMostrarClave] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorGeneral, setErrorGeneral] = useState('');
  const [geolocalizando, setGeolocalizando] = useState(false);

  const [chatIA, setChatIA] = useState([]);
  const [inputIA, setInputIA] = useState('');
  const [enviandoIA, setEnviandoIA] = useState(false);
  const [iaListo, setIaListo] = useState(false);
  const [iaConfig, setIaConfig] = useState(null);
  const [generandoConfig, setGenerandoConfig] = useState(false);
  const [iaFase, setIaFase] = useState('chat');

  const debouncedNombre = useDebounce(empresa.nombre, 600);
  const debouncedCedula = useDebounce(admin.cedula, 600);
  const debouncedUsuario = useDebounce(admin.usuario, 600);
  const debouncedCorreo = useDebounce(admin.correo, 600);

  const validarRemoto = useCallback(async (campo, valor, setter) => {
    if (!valor || valor.trim().length < 2) {
      setter({ cargando: false, disponible: null });
      return;
    }
    setter((prev) => ({ ...prev, cargando: true }));
    try {
      const res = await API.get(
        `/publico/validar-registro?campo=${campo}&valor=${encodeURIComponent(valor.trim())}`
      );
      const data = res.data.data;
      if (campo === 'slug') {
        setter({
          cargando: false,
          disponible: data.disponible,
          sugerido: data.slug_sugerido || '',
        });
      } else {
        setter({ cargando: false, disponible: data.disponible });
      }
    } catch {
      setter({ cargando: false, disponible: null });
    }
  }, []);

  useEffect(() => {
    if (debouncedNombre.trim().length >= 2) validarRemoto('slug', debouncedNombre, setSlugVal);
    else setSlugVal({ cargando: false, disponible: null, sugerido: '' });
  }, [debouncedNombre, validarRemoto]);

  useEffect(() => {
    if (debouncedCedula.trim().length >= 5) validarRemoto('cedula', debouncedCedula, setCedulaVal);
    else setCedulaVal({ cargando: false, disponible: null });
  }, [debouncedCedula, validarRemoto]);

  useEffect(() => {
    if (debouncedUsuario.trim().length >= 3)
      validarRemoto('usuario', debouncedUsuario, setUsuarioVal);
    else setUsuarioVal({ cargando: false, disponible: null });
  }, [debouncedUsuario, validarRemoto]);

  useEffect(() => {
    if (
      debouncedCorreo.trim().length >= 5 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedCorreo.trim())
    )
      validarRemoto('correo', debouncedCorreo, setCorreoVal);
    else setCorreoVal({ cargando: false, disponible: null });
  }, [debouncedCorreo, validarRemoto]);

  useEffect(() => {
    if (plan.tipo === 'externo') setConfig((p) => ({ ...p, inventario_tipo: 'externo' }));
  }, [plan.tipo]);

  const setEmp = (k, v) => {
    setEmpresa((p) => ({ ...p, [k]: v }));
    setErrores((p) => {
      const n = { ...p };
      delete n[k];
      return n;
    });
  };
  const setAdm = (k, v) => {
    setAdmin((p) => ({ ...p, [k]: v }));
    setErrores((p) => {
      const n = { ...p };
      delete n[k];
      return n;
    });
  };
  const setSed = (k, v) => {
    setSede((p) => ({ ...p, [k]: v }));
    setErrores((p) => {
      const n = { ...p };
      delete n[k];
      return n;
    });
  };
  const setCfg = (k, v) => {
    setConfig((p) => ({ ...p, [k]: v }));
    setErrores((p) => {
      const n = { ...p };
      delete n[k];
      return n;
    });
  };
  const setPlanField = (k, v) => {
    setPlan((p) => ({ ...p, [k]: v }));
    setErrores((p) => {
      const n = { ...p };
      delete n[k];
      return n;
    });
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setErrores((p) => ({ ...p, logo: 'Formato no soportado. Usa PNG, JPG o WebP.' }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrores((p) => ({ ...p, logo: 'El logo supera el tamaño máximo de 2 MB.' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo({ base64: reader.result, preview: reader.result });
    reader.readAsDataURL(file);
    setErrores((p) => {
      const n = { ...p };
      delete n.logo;
      return n;
    });
  };

  const quitarLogo = () => {
    setLogo({ base64: null, preview: null });
    setErrores((p) => {
      const n = { ...p };
      delete n.logo;
      return n;
    });
  };

  const validarPlan = () => {
    const e = {};
    if (plan.tipo === 'externo' && plan.integracion_externa_url.trim()) {
      try {
        const u = new URL(plan.integracion_externa_url.trim());
        if (u.protocol !== 'https:')
          e.integracion_externa_url = 'La URL debe comenzar con https://';
      } catch {
        e.integracion_externa_url = 'URL inválida';
      }
    }
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const validarEmpresa = () => {
    const e = {};
    if (!empresa.nombre.trim()) e.nombre = 'Nombre de empresa requerido';
    else if (empresa.nombre.trim().length < 3) e.nombre = 'Mínimo 3 caracteres';
    if (!empresa.nombre_representante.trim())
      e.nombre_representante = 'Nombre del representante requerido';
    if (!empresa.ci_rif.trim()) e.ci_rif = 'CI/RIF requerido';
    if (!empresa.telefono.trim()) e.telefono = 'Teléfono requerido';
    else if (empresa.telefono.replace(/\D/g, '').length < 7) e.telefono = 'Teléfono muy corto';
    if (slugVal.disponible === false) e.nombre = 'Ya existe una empresa con nombre similar';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const validarAdmin = () => {
    const e = {};
    if (!admin.nombre.trim()) e.admin_nombre = 'Nombre requerido';
    if (!admin.cedula.trim()) e.admin_cedula = 'Cédula requerida';
    else if (admin.cedula.trim().length < 5) e.admin_cedula = 'Mínimo 5 caracteres';
    if (cedulaVal.disponible === false) e.admin_cedula = 'Esta cédula ya está registrada';
    if (!admin.usuario.trim()) e.admin_usuario = 'Usuario requerido';
    else if (admin.usuario.trim().length < 3) e.admin_usuario = 'Mínimo 3 caracteres';
    if (usuarioVal.disponible === false) e.admin_usuario = 'Este usuario ya está en uso';
    if (!admin.correo.trim()) e.admin_correo = 'Correo requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.correo)) e.admin_correo = 'Correo inválido';
    if (correoVal.disponible === false) e.admin_correo = 'Este correo ya está registrado';
    if (!admin.clave) e.admin_clave = 'Contraseña requerida';
    else if (admin.clave.length < 4) e.admin_clave = 'Mínimo 4 caracteres';
    if (admin.clave !== admin.confirmar_clave) e.admin_confirmar = 'Las contraseñas no coinciden';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const validarSede = () => {
    const e = {};
    if (!sede.nombre.trim()) e.sede_nombre = 'Nombre de sede requerido';
    if (!sede.direccion.trim()) e.sede_direccion = 'Dirección requerida';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const validarConfig = () => {
    const e = {};
    if (config.tipos_delivery.length === 0) e.tipos_delivery = 'Selecciona al menos un tipo';
    if (monedas.length === 0) e.monedas = 'Agrega al menos una moneda';
    else if (!monedas.some((m) => m.es_principal)) e.monedas = 'Selecciona una moneda principal';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const siguiente = () => {
    setErrores({});
    if (paso === 1 && validarPlan()) setPaso(2);
    else if (paso === 2 && validarEmpresa()) setPaso(3);
    else if (paso === 3 && validarAdmin()) setPaso(4);
    else if (paso === 4 && validarSede()) setPaso(5);
    else if (paso === 5 && validarConfig()) {
      setPaso(6);
      iniciarChatIA();
    }
  };

  const anterior = () => {
    if (paso > 1) {
      setPaso(paso - 1);
      setErrores({});
    } else nav('/');
  };

  const toggleDelivery = (tipo) => {
    setCfg(
      'tipos_delivery',
      config.tipos_delivery.includes(tipo)
        ? config.tipos_delivery.filter((t) => t !== tipo)
        : [...config.tipos_delivery, tipo]
    );
  };

  const toggleMoneda = (mp) => {
    const existe = monedas.find((m) => m.codigo === mp.codigo);
    if (existe) {
      const nuevas = monedas.filter((m) => m.codigo !== mp.codigo);
      if (existe.es_principal && nuevas.length > 0) nuevas[0].es_principal = true;
      setMonedas(nuevas);
    } else {
      setMonedas([...monedas, { ...mp, tasa_cambio: 1, es_principal: monedas.length === 0 }]);
    }
  };

  const setPrincipal = (codigo) => {
    setMonedas(
      monedas.map((m) => ({
        ...m,
        es_principal: m.codigo === codigo,
        tasa_cambio: m.codigo === codigo ? 1 : m.tasa_cambio,
      }))
    );
  };

  const setTasa = (codigo, tasa) => {
    setMonedas(
      monedas.map((m) => (m.codigo === codigo ? { ...m, tasa_cambio: parseFloat(tasa) || 0 } : m))
    );
  };

  const agregarCustom = () => {
    if (!monedaCustom.nombre.trim() || !monedaCustom.codigo.trim() || !monedaCustom.simbolo.trim())
      return;
    if (monedas.find((m) => m.codigo === monedaCustom.codigo.toUpperCase())) return;
    setMonedas([
      ...monedas,
      {
        nombre: monedaCustom.nombre.trim(),
        codigo: monedaCustom.codigo.toUpperCase().trim(),
        simbolo: monedaCustom.simbolo.trim(),
        tasa_cambio: 1,
        es_principal: monedas.length === 0,
      },
    ]);
    setMonedaCustom({ nombre: '', codigo: '', simbolo: '' });
  };

  const obtenerUbicacion = () => {
    if (!navigator.geolocation) return;
    setGeolocalizando(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSede((p) => ({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setGeolocalizando(false);
      },
      () => setGeolocalizando(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const iniciarChatIA = async () => {
    if (chatIA.length > 0) return;
    setEnviandoIA(true);
    try {
      const res = await API.post('/publico/onboarding-ia/chat', {
        mensajes: [],
        contexto: { nombre_empresa: empresa.nombre, tipo_inventario: config.inventario_tipo },
      });
      if (res.data.status === 'success')
        setChatIA([{ rol: 'asistente', contenido: res.data.data.respuesta }]);
    } catch {
    } finally {
      setEnviandoIA(false);
    }
  };

  const enviarMensajeIA = async (mensaje) => {
    if (!mensaje?.trim() || enviandoIA) return;
    const nuevos = [...chatIA, { rol: 'usuario', contenido: mensaje.trim() }];
    setChatIA(nuevos);
    setInputIA('');
    setEnviandoIA(true);
    try {
      const res = await API.post('/publico/onboarding-ia/chat', {
        mensajes: nuevos,
        contexto: { nombre_empresa: empresa.nombre, tipo_inventario: config.inventario_tipo },
      });
      if (res.data.status === 'success') {
        setChatIA([...nuevos, { rol: 'asistente', contenido: res.data.data.respuesta }]);
        if (res.data.data.listo) setIaListo(true);
      }
    } catch {
    } finally {
      setEnviandoIA(false);
    }
  };

  const generarConfigIA = async () => {
    setGenerandoConfig(true);
    try {
      const res = await API.post('/publico/onboarding-ia/generar-config', {
        mensajes: chatIA,
        contexto: { nombre_empresa: empresa.nombre, tipo_inventario: config.inventario_tipo },
      });
      if (res.data.status === 'success') {
        setIaConfig(res.data.data);
        setIaFase('preview');
      }
    } catch {
    } finally {
      setGenerandoConfig(false);
    }
  };

  const enviarRegistro = async () => {
    if (paso === 5 && !validarConfig()) return;
    setEnviando(true);
    setErrorGeneral('');
    try {
      const body = {
        empresa: {
          nombre: empresa.nombre.trim(),
          nombre_representante: empresa.nombre_representante.trim(),
          ci_rif: empresa.ci_rif.trim(),
          telefono: empresa.telefono.trim(),
          codigo_pais: empresa.codigo_pais,
        },
        admin: {
          nombre: admin.nombre.trim(),
          cedula: admin.cedula.trim(),
          usuario: admin.usuario.trim(),
          correo: admin.correo.trim(),
          clave: admin.clave,
        },
        sede: {
          nombre: sede.nombre.trim(),
          direccion: sede.direccion.trim(),
          lat: sede.lat,
          lng: sede.lng,
        },
        config: {
          inventario_tipo: plan.tipo === 'externo' ? 'externo' : config.inventario_tipo,
          tipos_delivery: config.tipos_delivery,
          modo: config.modo,
          nombre_app: config.nombre_app.trim() || null,
          color_primario: config.color_primario,
          color_secundario: config.color_secundario,
          plan_tipo: plan.tipo,
          integracion_externa_url:
            plan.tipo === 'externo' && plan.integracion_externa_url.trim()
              ? plan.integracion_externa_url.trim()
              : null,
          logo_base64: logo.base64 || null,
          tipo_empresa: config.tipo_empresa || 'general',
        },
        monedas: monedas.map((m) => ({
          nombre: m.nombre,
          codigo: m.codigo,
          simbolo: m.simbolo,
          tasa_cambio: m.tasa_cambio,
          es_principal: m.es_principal,
        })),
      };
      if (iaConfig) body.ia_config = iaConfig;
      const res = await API.post('/publico/registro-empresa', body);
      if (res.data.status === 'success') setResultado(res.data.data);
      else setErrorGeneral(res.data.message || 'Error al registrar');
    } catch (err) {
      setErrorGeneral(err.response?.data?.detail || 'Error de conexión. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const renderIndicadorValido = (val) => {
    if (val.cargando)
      return (
        <div style={S.valIndicator}>
          <div style={S.spinner} />
        </div>
      );
    if (val.disponible === true)
      return (
        <div style={S.valIndicator}>
          <FiCheck size={14} color={COL.green} />
        </div>
      );
    if (val.disponible === false)
      return (
        <div style={S.valIndicator}>
          <FiX size={14} color={COL.red} />
        </div>
      );
    return null;
  };

  if (resultado) {
    return (
      <div style={S.page}>
        <style>{`*{box-sizing:border-box}body{margin:0;overflow:auto!important;height:auto!important}html{overflow:auto!important;height:auto!important}`}</style>
        <div style={S.body}>
          <div style={S.container}>
            <div style={{ ...S.card, textAlign: 'center' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: COL.greenBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <FiCheck size={32} color={COL.green} />
              </div>
              <h1 style={{ ...S.title, color: COL.green }}>Registro Exitoso</h1>
              <p style={{ ...S.subtitle, marginBottom: '24px' }}>
                Tu empresa ha sido creada correctamente.
              </p>

              <div
                style={{
                  background: COL.bg,
                  borderRadius: '14px',
                  padding: '20px',
                  textAlign: 'left',
                  marginBottom: '20px',
                }}
              >
                <div style={S.resRow}>
                  <span style={S.resLabel}>Empresa</span>
                  <span style={S.resValue}>{resultado.nombre}</span>
                </div>
                <div style={S.resRow}>
                  <span style={S.resLabel}>Subdominio</span>
                  <a
                    href={`https://${resultado.subdominio}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...S.resValue, color: COL.acc, textDecoration: 'none' }}
                  >
                    {resultado.subdominio}
                  </a>
                </div>
                <div style={S.resRow}>
                  <span style={S.resLabel}>Usuario admin</span>
                  <span style={S.resValue}>{resultado.admin_usuario}</span>
                </div>
                <div style={S.resRow}>
                  <span style={S.resLabel}>Cédula admin</span>
                  <span style={S.resValue}>{resultado.admin_cedula}</span>
                </div>
                <div style={S.resRow}>
                  <span style={S.resLabel}>Sede</span>
                  <span style={S.resValue}>{resultado.sede_codigo}</span>
                </div>
              </div>

              <div
                style={{
                  background: COL.amberBg,
                  borderRadius: '12px',
                  padding: '16px',
                  border: `1px solid ${COL.amber}30`,
                  marginBottom: '24px',
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: COL.amber,
                    marginBottom: '4px',
                  }}
                >
                  Trial Gratuito — 14 días
                </div>
                <div style={{ fontSize: '13px', color: '#92400e' }}>
                  Desde {resultado.trial?.fecha_inicio} hasta {resultado.trial?.fecha_fin}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => window.open(`https://${resultado.subdominio}`, '_blank')}
                  style={S.nextBtn}
                >
                  Ir a tu tienda <FiArrowRight size={16} />
                </button>
                <button
                  onClick={() => window.open(getAdminUrl(), '_blank')}
                  style={{
                    ...S.nextBtn,
                    background: `linear-gradient(135deg, ${COL.pri}, ${COL.pri2})`,
                  }}
                >
                  Ir al panel de administración <FiArrowRight size={16} />
                </button>
                <button onClick={() => nav('/')} style={S.backBtn}>
                  <FiArrowLeft size={16} /> Volver al inicio
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}body{margin:0;overflow:auto!important;height:auto!important}html{overflow:auto!important;height:auto!important}`}</style>

      <div style={S.header}>
        <div
          style={{
            ...S.container,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: '20px',
              color: COL.white,
              cursor: 'pointer',
              letterSpacing: '-0.5px',
            }}
            onClick={() => nav('/')}
          >
            AGORAX
          </div>
          <button onClick={() => nav('/')} style={S.cancelBtn}>
            Cancelar
          </button>
        </div>
      </div>

      <div style={S.body}>
        <div style={S.container}>
          <div style={S.card}>
            <h1 style={S.title}>Registra tu Empresa</h1>
            <p style={S.subtitle}>Prueba gratis por 14 días. Sin tarjeta de crédito.</p>

            <div style={S.stepper}>
              {PASOS.map((p, i) => {
                const activo = paso === p.n;
                const completo = paso > p.n;
                return (
                  <React.Fragment key={p.n}>
                    {i > 0 && (
                      <div
                        style={{ ...S.stepLine, background: completo ? COL.green : COL.border }}
                      />
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <div
                        style={{
                          width: mobile ? '32px' : '36px',
                          height: mobile ? '32px' : '36px',
                          borderRadius: '50%',
                          background: completo
                            ? COL.green
                            : activo
                              ? `linear-gradient(135deg,${COL.pri2},${COL.acc})`
                              : COL.bg,
                          border: activo ? 'none' : completo ? 'none' : `2px solid ${COL.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: completo || activo ? COL.white : COL.muted,
                          fontSize: '13px',
                          fontWeight: 700,
                          transition: 'all 0.3s',
                        }}
                      >
                        {completo ? <FiCheck size={14} /> : p.n}
                      </div>
                      {!mobile && (
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: activo ? 700 : 500,
                            color: activo ? COL.pri2 : completo ? COL.green : COL.muted,
                          }}
                        >
                          {p.label}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {paso === 1 && (
              <div style={S.stepContent}>
                <h2 style={S.stepTitle}>Elige tu Plan</h2>
                <p style={S.stepDesc}>
                  El plan define qué módulos podrás usar en Agorax. Puedes cambiarlo después con tu
                  administrador.
                </p>

                <div style={{ ...S.planGrid, gridTemplateColumns: mobile ? '1fr' : '1fr 1fr' }}>
                  {[
                    {
                      v: 'interno',
                      icon: FiLayers,
                      titulo: 'Plan Interno',
                      sub: 'Acceso total',
                      features: [
                        { ok: true, t: 'Catálogo y productos (crear/editar)' },
                        { ok: true, t: 'Inventario, stock y combos' },
                        { ok: true, t: 'Punto de venta (POS) y caja' },
                        { ok: true, t: 'Pedidos y delivery' },
                        { ok: true, t: 'Chat y bot de IA' },
                        { ok: true, t: 'Reportes y config avanzada' },
                      ],
                    },
                    {
                      v: 'externo',
                      icon: FiTruck,
                      titulo: 'Plan Externo',
                      sub: 'Solo ventas y delivery',
                      features: [
                        { ok: true, t: 'Panel de ventas y pedidos' },
                        { ok: true, t: 'Gestion de delivery' },
                        { ok: true, t: 'Chat y bot de IA' },
                        { ok: true, t: 'Catálogo en solo lectura' },
                        { ok: true, t: 'Inventario sincronizado externo' },
                        { ok: false, t: 'No gestiona productos en Agorax' },
                      ],
                    },
                  ].map((p) => {
                    const sel = plan.tipo === p.v;
                    const Icon = p.icon;
                    return (
                      <div
                        key={p.v}
                        onClick={() => setPlanField('tipo', p.v)}
                        style={{ ...S.planCard, ...(sel ? S.planCardActive : {}) }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '10px',
                          }}
                        >
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              background: sel ? COL.acc : COL.bg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={18} color={sel ? COL.white : COL.muted} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: '15px',
                                fontWeight: 700,
                                color: sel ? COL.pri2 : COL.text,
                              }}
                            >
                              {p.titulo}
                            </div>
                            <div style={{ fontSize: '12px', color: COL.muted }}>{p.sub}</div>
                          </div>
                          {sel && (
                            <div style={{ marginLeft: 'auto' }}>
                              <FiCheck size={18} color={COL.green} />
                            </div>
                          )}
                        </div>
                        <div>
                          {p.features.map((f, i) => (
                            <div key={i} style={S.planFeature}>
                              {f.ok ? (
                                <FiCheck size={12} color={COL.green} />
                              ) : (
                                <FiX size={12} color={COL.red} />
                              )}
                              <span style={{ color: f.ok ? COL.text : COL.muted }}>{f.t}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {plan.tipo === 'externo' && (
                  <div style={S.fieldGroup}>
                    <label style={S.label}>URL de integración (opcional)</label>
                    <div style={S.inputWrap}>
                      <input
                        style={S.input}
                        placeholder="https://tu-sistema.com/api"
                        value={plan.integracion_externa_url}
                        onChange={(e) => setPlanField('integracion_externa_url', e.target.value)}
                      />
                    </div>
                    {errores.integracion_externa_url && (
                      <span style={S.errorText}>{errores.integracion_externa_url}</span>
                    )}
                    <span style={{ ...S.hintText, color: COL.muted }}>
                      Si tu sistema externo expone una API para sincronizar inventario, ingresala
                      (https). Puedes dejarlo vacio y configurarlo luego.
                    </span>
                  </div>
                )}
              </div>
            )}

            {paso === 2 && (
              <div style={S.stepContent}>
                <h2 style={S.stepTitle}>Datos de la Empresa</h2>
                <p style={S.stepDesc}>Información básica de tu negocio.</p>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Logo de la empresa</label>
                  <div style={S.logoRow}>
                    <div style={S.logoPreview}>
                      {logo.preview ? (
                        <img src={logo.preview} alt="logo" style={S.logoImg} />
                      ) : (
                        <FiImage size={28} color={COL.muted} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={S.logoBtn}>
                          <FiUpload size={14} /> {logo.preview ? 'Cambiar logo' : 'Subir logo'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={onLogoChange}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {logo.preview && (
                          <button type="button" onClick={quitarLogo} style={S.logoRemove}>
                            Quitar
                          </button>
                        )}
                      </div>
                      <span style={{ ...S.hintText, color: COL.muted, marginTop: '6px' }}>
                        PNG, JPG o WebP. Máximo 2 MB. Se mostrará en tu tienda y panel.
                      </span>
                    </div>
                  </div>
                  {errores.logo && <span style={S.errorText}>{errores.logo}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Nombre de la empresa *</label>
                  <div style={S.inputWrap}>
                    <FiBriefcase size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: Mi Tienda Online"
                      value={empresa.nombre}
                      onChange={(e) => setEmp('nombre', e.target.value)}
                    />
                    {renderIndicadorValido(slugVal)}
                  </div>
                  {errores.nombre && <span style={S.errorText}>{errores.nombre}</span>}
                  {slugVal.disponible === true && slugVal.sugerido && (
                    <span style={S.hintText}>
                      Tu subdominio será: <strong>{empresaDominio(slugVal.sugerido)}</strong>
                    </span>
                  )}
                  {slugVal.disponible === false && slugVal.sugerido && (
                    <span style={{ ...S.hintText, color: COL.red }}>
                      Sugerencia disponible: <strong>{empresaDominio(slugVal.sugerido)}</strong>
                    </span>
                  )}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Nombre del representante *</label>
                  <div style={S.inputWrap}>
                    <FiUser size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Nombre completo"
                      value={empresa.nombre_representante}
                      onChange={(e) => setEmp('nombre_representante', e.target.value)}
                    />
                  </div>
                  {errores.nombre_representante && (
                    <span style={S.errorText}>{errores.nombre_representante}</span>
                  )}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>CI / RIF *</label>
                  <div style={S.inputWrap}>
                    <FiBriefcase size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: V-12345678 o J-12345678-9"
                      value={empresa.ci_rif}
                      onChange={(e) => setEmp('ci_rif', e.target.value)}
                    />
                  </div>
                  {errores.ci_rif && <span style={S.errorText}>{errores.ci_rif}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Teléfono de contacto *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      style={S.select}
                      value={empresa.codigo_pais}
                      onChange={(e) => setEmp('codigo_pais', e.target.value)}
                    >
                      {PAISES.map((p) => (
                        <option key={p.codigo} value={p.codigo}>
                          {p.bandera} {p.codigo}
                        </option>
                      ))}
                    </select>
                    <div style={{ ...S.inputWrap, flex: 1 }}>
                      <FiPhone size={16} color={COL.muted} />
                      <input
                        style={S.input}
                        placeholder="4121234567"
                        type="tel"
                        value={empresa.telefono}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          if (v.length <= 15) setEmp('telefono', v);
                        }}
                      />
                    </div>
                  </div>
                  {errores.telefono && <span style={S.errorText}>{errores.telefono}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Tipo de empresa *</label>
                  <p style={{ ...S.stepDesc, marginBottom: '10px', fontSize: '13px' }}>
                    Define el rubro de tu negocio. Esto configura las opciones y herramientas
                    disponibles en tu panel.
                  </p>
                  <div style={S.tipoGrid}>
                    {[
                      { key: 'general', titulo: 'General', desc: 'Sin especializacion. Todas las opciones basicas disponibles.' },
                      { key: 'restaurante', titulo: 'Restaurante', desc: 'Manejo de mesas, cocina, meseros y servicio en sitio.' },
                      { key: 'importador', titulo: 'Importador', desc: 'Armado de pedidos, catalogo al mayor y entregas.' },
                    ].map((t) => (
                      <div
                        key={t.key}
                        onClick={() => setConfig({ ...config, tipo_empresa: t.key })}
                        style={{
                          ...S.tipoCard,
                          border: config.tipo_empresa === t.key
                            ? `2px solid ${COL.acc}`
                            : `2px solid ${COL.border}`,
                          background: config.tipo_empresa === t.key
                            ? `${COL.acc}08`
                            : COL.white,
                        }}
                      >
                        <div style={S.tipoCardHeader}>
                          <div
                            style={{
                              ...S.tipoRadio,
                              borderColor: config.tipo_empresa === t.key ? COL.acc : COL.muted,
                              background: config.tipo_empresa === t.key ? COL.acc : 'transparent',
                            }}
                          >
                            {config.tipo_empresa === t.key && (
                              <div style={S.tipoRadioDot} />
                            )}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: '14px', color: COL.text }}>
                            {t.titulo}
                          </span>
                        </div>
                        <p style={{ fontSize: '12px', color: COL.muted, margin: '4px 0 0', lineHeight: 1.4 }}>
                          {t.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {paso === 3 && (
              <div style={S.stepContent}>
                <h2 style={S.stepTitle}>Usuario Administrador</h2>
                <p style={S.stepDesc}>
                  Este será el usuario principal con control total sobre la empresa.
                </p>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Nombre completo *</label>
                  <div style={S.inputWrap}>
                    <FiUser size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Nombre y apellido"
                      value={admin.nombre}
                      onChange={(e) => setAdm('nombre', e.target.value)}
                    />
                  </div>
                  {errores.admin_nombre && <span style={S.errorText}>{errores.admin_nombre}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Cédula *</label>
                  <div style={S.inputWrap}>
                    <FiBriefcase size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: 12345678"
                      value={admin.cedula}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        if (v.length <= 12) setAdm('cedula', v);
                      }}
                    />
                    {renderIndicadorValido(cedulaVal)}
                  </div>
                  {errores.admin_cedula && <span style={S.errorText}>{errores.admin_cedula}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Nombre de usuario *</label>
                  <div style={S.inputWrap}>
                    <FiUser size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: juanadmin"
                      value={admin.usuario}
                      onChange={(e) =>
                        setAdm('usuario', e.target.value.toLowerCase().replace(/\s/g, ''))
                      }
                    />
                    {renderIndicadorValido(usuarioVal)}
                  </div>
                  {errores.admin_usuario && (
                    <span style={S.errorText}>{errores.admin_usuario}</span>
                  )}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Correo electrónico *</label>
                  <div style={S.inputWrap}>
                    <FiMail size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="correo@ejemplo.com"
                      type="email"
                      value={admin.correo}
                      onChange={(e) => setAdm('correo', e.target.value)}
                    />
                    {renderIndicadorValido(correoVal)}
                  </div>
                  {errores.admin_correo && <span style={S.errorText}>{errores.admin_correo}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Contraseña *</label>
                  <div style={S.inputWrap}>
                    <FiLock size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Mínimo 4 caracteres"
                      type={mostrarClave ? 'text' : 'password'}
                      value={admin.clave}
                      onChange={(e) => setAdm('clave', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarClave(!mostrarClave)}
                      style={S.eyeBtn}
                    >
                      {mostrarClave ? (
                        <FiEyeOff size={16} color={COL.muted} />
                      ) : (
                        <FiEye size={16} color={COL.muted} />
                      )}
                    </button>
                  </div>
                  {errores.admin_clave && <span style={S.errorText}>{errores.admin_clave}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Confirmar contraseña *</label>
                  <div style={S.inputWrap}>
                    <FiLock size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Repite la contraseña"
                      type={mostrarConfirmar ? 'text' : 'password'}
                      value={admin.confirmar_clave}
                      onChange={(e) => setAdm('confirmar_clave', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                      style={S.eyeBtn}
                    >
                      {mostrarConfirmar ? (
                        <FiEyeOff size={16} color={COL.muted} />
                      ) : (
                        <FiEye size={16} color={COL.muted} />
                      )}
                    </button>
                  </div>
                  {errores.admin_confirmar && (
                    <span style={S.errorText}>{errores.admin_confirmar}</span>
                  )}
                  {admin.clave &&
                    admin.confirmar_clave &&
                    admin.clave === admin.confirmar_clave && (
                      <span style={S.hintText}>
                        <FiCheck
                          size={12}
                          color={COL.green}
                          style={{ verticalAlign: 'middle', marginRight: '4px' }}
                        />
                        Las contraseñas coinciden
                      </span>
                    )}
                </div>
              </div>
            )}

            {paso === 4 && (
              <div style={S.stepContent}>
                <h2 style={S.stepTitle}>Primera Sede</h2>
                <p style={S.stepDesc}>Datos y ubicación de tu primera sucursal.</p>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Nombre de la sede *</label>
                  <div style={S.inputWrap}>
                    <FiMapPin size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: Sede Principal"
                      value={sede.nombre}
                      onChange={(e) => setSed('nombre', e.target.value)}
                    />
                  </div>
                  {errores.sede_nombre && <span style={S.errorText}>{errores.sede_nombre}</span>}
                </div>

                <div style={S.fieldGroup}>
                  <label style={S.label}>Dirección *</label>
                  <div style={S.inputWrap}>
                    <FiMapPin size={16} color={COL.muted} />
                    <input
                      style={S.input}
                      placeholder="Ej: Av. Principal, Centro Comercial..."
                      value={sede.direccion}
                      onChange={(e) => setSed('direccion', e.target.value)}
                    />
                  </div>
                  {errores.sede_direccion && (
                    <span style={S.errorText}>{errores.sede_direccion}</span>
                  )}
                </div>

                <div style={S.fieldGroup}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <label style={{ ...S.label, margin: 0 }}>Ubicación en el mapa</label>
                    <button
                      onClick={obtenerUbicacion}
                      disabled={geolocalizando}
                      style={{
                        background: 'none',
                        border: `1px solid ${COL.acc}`,
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: COL.acc,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {geolocalizando ? (
                        <>
                          <div style={{ ...S.spinner, width: '12px', height: '12px' }} />{' '}
                          Obteniendo...
                        </>
                      ) : (
                        <>
                          <FiNavigation size={12} /> Mi ubicación
                        </>
                      )}
                    </button>
                  </div>
                  <MapaUbicacion
                    lat={sede.lat}
                    lng={sede.lng}
                    readOnly={false}
                    onLocationChange={(lat, lng) => setSede((p) => ({ ...p, lat, lng }))}
                    height="220px"
                  />
                  <span style={{ ...S.hintText, color: COL.muted, marginTop: '6px' }}>
                    Haz clic o arrastra el marcador para ajustar la ubicación. Lat:{' '}
                    {sede.lat?.toFixed(4)}, Lng: {sede.lng?.toFixed(4)}
                  </span>
                </div>
              </div>
            )}

            {paso === 5 && (
              <div style={S.stepContent}>
                <h2 style={S.stepTitle}>Configuración</h2>
                <p style={S.stepDesc}>Personaliza el funcionamiento de tu negocio.</p>

                {plan.tipo === 'interno' ? (
                  <div style={S.section}>
                    <div style={S.sectionTitle}>Tipo de inventario</div>
                    <p style={S.helpText}>
                      Como manejas tu stock: gestionado manualmente en Agorax o sincronizado desde
                      tu sistema.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[
                        { v: 'interno', l: 'Manual', d: 'Gestion manual desde Agorax' },
                        { v: 'externo', l: 'Sincronizado', d: 'Sincronizado con tu sistema' },
                      ].map((opt) => (
                        <div
                          key={opt.v}
                          onClick={() => setCfg('inventario_tipo', opt.v)}
                          style={{
                            ...S.optionCard,
                            border:
                              config.inventario_tipo === opt.v
                                ? `2px solid ${COL.acc}`
                                : `1px solid ${COL.border}`,
                            background:
                              config.inventario_tipo === opt.v ? `${COL.acc}08` : COL.white,
                            cursor: 'pointer',
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: config.inventario_tipo === opt.v ? COL.pri2 : COL.text,
                            }}
                          >
                            {opt.l}
                          </div>
                          <div style={{ fontSize: '12px', color: COL.muted, marginTop: '2px' }}>
                            {opt.d}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={S.section}>
                    <div style={S.sectionTitle}>Inventario</div>
                    <div style={S.notaBox}>
                      <FiPackage
                        size={16}
                        color={COL.amber}
                        style={{ flexShrink: 0, marginTop: '1px' }}
                      />
                      <span>
                        Tu inventario está sincronizado con tu sistema externo. No podrás gestionar
                        productos desde Agorax (Plan Externo).
                      </span>
                    </div>
                  </div>
                )}

                <div style={S.section}>
                  <div style={S.sectionTitle}>Tipos de entrega *</div>
                  <p style={S.helpText}>
                    Cómo entregas los pedidos. Tus clientes eligen una de estas opciones al
                    confirmar su compra.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { v: 'retiro', l: 'Retiro en sede' },
                      { v: 'delivery', l: 'Delivery' },
                      { v: 'encomienda', l: 'Encomienda' },
                    ].map((opt) => {
                      const sel = config.tipos_delivery.includes(opt.v);
                      return (
                        <div
                          key={opt.v}
                          onClick={() => toggleDelivery(opt.v)}
                          style={{
                            ...S.chip,
                            background: sel ? `${COL.acc}15` : COL.bg,
                            border: sel ? `2px solid ${COL.acc}` : `1px solid ${COL.border}`,
                            color: sel ? COL.pri2 : COL.muted,
                            cursor: 'pointer',
                          }}
                        >
                          {sel && <FiCheck size={12} />} {opt.l}
                        </div>
                      );
                    })}
                  </div>
                  {errores.tipos_delivery && (
                    <span style={S.errorText}>{errores.tipos_delivery}</span>
                  )}
                </div>

                <div style={S.section}>
                  <div style={S.sectionTitle}>Modo de atención</div>
                  <p style={S.helpText}>
                    Cómo interactuan tus clientes: navegando el catálogo, chateando con el bot, o
                    ambos.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { v: 'ambos', l: 'Catálogo + Chat' },
                      { v: 'solo_catalogo', l: 'Solo Catálogo' },
                      { v: 'solo_chat', l: 'Solo Chat' },
                    ].map((opt) => (
                      <div
                        key={opt.v}
                        onClick={() => setCfg('modo', opt.v)}
                        style={{
                          ...S.chip,
                          background: config.modo === opt.v ? `${COL.acc}15` : COL.bg,
                          border:
                            config.modo === opt.v
                              ? `2px solid ${COL.acc}`
                              : `1px solid ${COL.border}`,
                          color: config.modo === opt.v ? COL.pri2 : COL.muted,
                          cursor: 'pointer',
                        }}
                      >
                        {config.modo === opt.v && <FiCheck size={12} />} {opt.l}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={S.section}>
                  <div style={S.sectionTitle}>Monedas *</div>
                  <p style={S.helpText}>
                    Monedas en las que cobras. Marca una como principal; las demás usan su tasa
                    relativa.
                  </p>
                  <div
                    style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}
                  >
                    {MONEDAS_PRED.map((mp) => {
                      const sel = monedas.find((m) => m.codigo === mp.codigo);
                      return (
                        <div
                          key={mp.codigo}
                          onClick={() => toggleMoneda(mp)}
                          style={{
                            ...S.chip,
                            fontSize: '12px',
                            padding: '6px 10px',
                            background: sel ? `${COL.acc}15` : COL.bg,
                            border: sel ? `2px solid ${COL.acc}` : `1px solid ${COL.border}`,
                            color: sel ? COL.pri2 : COL.muted,
                            cursor: 'pointer',
                          }}
                        >
                          {mp.simbolo} {mp.codigo}
                        </div>
                      );
                    })}
                  </div>

                  {monedas.length > 0 && (
                    <div
                      style={{
                        background: COL.bg,
                        borderRadius: '10px',
                        padding: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      {monedas.map((m) => (
                        <div
                          key={m.codigo}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 0',
                            borderBottom: `1px solid ${COL.border}`,
                          }}
                        >
                          <button
                            onClick={() => setPrincipal(m.codigo)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                            }}
                          >
                            <FiStar
                              size={16}
                              color={m.es_principal ? COL.amber : COL.border}
                              fill={m.es_principal ? COL.amber : 'none'}
                            />
                          </button>
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: COL.text,
                              minWidth: '60px',
                            }}
                          >
                            {m.simbolo} {m.codigo}
                          </span>
                          {m.es_principal ? (
                            <span style={{ fontSize: '11px', color: COL.green, fontWeight: 600 }}>
                              Principal
                            </span>
                          ) : (
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}
                            >
                              <span style={{ fontSize: '11px', color: COL.muted }}>Tasa:</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={m.tasa_cambio}
                                onChange={(e) => setTasa(m.codigo, e.target.value)}
                                style={{
                                  ...S.input,
                                  width: '80px',
                                  fontSize: '13px',
                                  background: COL.white,
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  border: `1px solid ${COL.border}`,
                                }}
                              />
                            </div>
                          )}
                          <button
                            onClick={() => toggleMoneda(m)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                              marginLeft: 'auto',
                            }}
                          >
                            <FiTrash2 size={14} color={COL.red} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {errores.monedas && <span style={S.errorText}>{errores.monedas}</span>}

                  <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        style={{
                          ...S.input,
                          background: COL.bg,
                          borderRadius: '8px',
                          padding: '8px 10px',
                          border: `1px solid ${COL.border}`,
                          fontSize: '12px',
                          width: '100%',
                        }}
                        placeholder="Nombre"
                        value={monedaCustom.nombre}
                        onChange={(e) => setMonedaCustom((p) => ({ ...p, nombre: e.target.value }))}
                      />
                    </div>
                    <input
                      style={{
                        ...S.input,
                        background: COL.bg,
                        borderRadius: '8px',
                        padding: '8px 10px',
                        border: `1px solid ${COL.border}`,
                        fontSize: '12px',
                        width: '60px',
                      }}
                      placeholder="COD"
                      maxLength={5}
                      value={monedaCustom.codigo}
                      onChange={(e) => setMonedaCustom((p) => ({ ...p, codigo: e.target.value }))}
                    />
                    <input
                      style={{
                        ...S.input,
                        background: COL.bg,
                        borderRadius: '8px',
                        padding: '8px 10px',
                        border: `1px solid ${COL.border}`,
                        fontSize: '12px',
                        width: '50px',
                      }}
                      placeholder="$"
                      maxLength={5}
                      value={monedaCustom.simbolo}
                      onChange={(e) => setMonedaCustom((p) => ({ ...p, simbolo: e.target.value }))}
                    />
                    <button
                      onClick={agregarCustom}
                      style={{
                        background: COL.acc,
                        color: COL.white,
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                      }}
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>
                  <span style={{ ...S.hintText, color: COL.muted, marginTop: '6px' }}>
                    Haz clic en la estrella para elegir la moneda principal. La tasa es relativa a
                    la principal.
                  </span>
                </div>

                <div style={S.section}>
                  <div style={S.sectionTitle}>Aspecto visual</div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Nombre de la app</label>
                    <div style={S.inputWrap}>
                      <input
                        style={S.input}
                        placeholder={empresa.nombre || 'Nombre que veran tus clientes'}
                        value={config.nombre_app}
                        onChange={(e) => setCfg('nombre_app', e.target.value)}
                      />
                    </div>
                    <span style={{ ...S.hintText, color: COL.muted }}>
                      Si lo dejas vacío se usará el nombre de la empresa.
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Color primario</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="color"
                          value={config.color_primario}
                          onChange={(e) => setCfg('color_primario', e.target.value)}
                          style={{
                            width: '40px',
                            height: '36px',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        />
                        <input
                          style={{
                            ...S.input,
                            background: COL.bg,
                            borderRadius: '8px',
                            padding: '8px 10px',
                            border: `1px solid ${COL.border}`,
                            fontSize: '13px',
                            width: '100%',
                          }}
                          value={config.color_primario}
                          onChange={(e) => setCfg('color_primario', e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Color secundario</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="color"
                          value={config.color_secundario}
                          onChange={(e) => setCfg('color_secundario', e.target.value)}
                          style={{
                            width: '40px',
                            height: '36px',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        />
                        <input
                          style={{
                            ...S.input,
                            background: COL.bg,
                            borderRadius: '8px',
                            padding: '8px 10px',
                            border: `1px solid ${COL.border}`,
                            fontSize: '13px',
                            width: '100%',
                          }}
                          value={config.color_secundario}
                          onChange={(e) => setCfg('color_secundario', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {paso === 6 && (
              <div style={S.stepContent}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '4px',
                  }}
                >
                  <h2 style={S.stepTitle}>Configura tu Bot de IA</h2>
                  {iaFase === 'chat' && !iaConfig && (
                    <button
                      onClick={enviarRegistro}
                      disabled={enviando}
                      style={{ ...S.backBtn, padding: '6px 14px', fontSize: '12px' }}
                    >
                      Omitir <FiArrowRight size={12} />
                    </button>
                  )}
                </div>
                <p style={S.stepDesc}>
                  {iaFase === 'chat'
                    ? 'Responde unas preguntas para personalizar tu bot de ventas.'
                    : 'Revisa y edita la configuración generada.'}
                </p>

                {iaFase === 'chat' && (
                  <div>
                    <div
                      style={{
                        background: COL.bg,
                        borderRadius: '14px',
                        padding: '16px',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        marginBottom: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      {chatIA.map((m, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            justifyContent: m.rol === 'usuario' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '80%',
                              padding: '10px 14px',
                              borderRadius: '14px',
                              fontSize: '14px',
                              lineHeight: 1.5,
                              background: m.rol === 'usuario' ? COL.pri2 : COL.white,
                              color: m.rol === 'usuario' ? COL.white : COL.text,
                              border: m.rol === 'usuario' ? 'none' : `1px solid ${COL.border}`,
                            }}
                            dangerouslySetInnerHTML={{ __html: fmtMd(m.contenido) }}
                          />
                        </div>
                      ))}
                      {enviandoIA && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <div
                            style={{
                              background: COL.white,
                              border: `1px solid ${COL.border}`,
                              borderRadius: '14px',
                              padding: '10px 14px',
                            }}
                          >
                            <div style={{ ...S.spinner, width: '16px', height: '16px' }} />
                          </div>
                        </div>
                      )}
                    </div>

                    {!iaListo ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ ...S.inputWrap, flex: 1 }}>
                          <input
                            style={S.input}
                            placeholder="Escribe tu respuesta..."
                            value={inputIA}
                            onChange={(e) => setInputIA(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !enviandoIA) enviarMensajeIA(inputIA);
                            }}
                            disabled={enviandoIA}
                          />
                        </div>
                        <button
                          onClick={() => enviarMensajeIA(inputIA)}
                          disabled={enviandoIA || !inputIA.trim()}
                          style={{
                            ...S.nextBtn,
                            padding: '10px 16px',
                            opacity: enviandoIA || !inputIA.trim() ? 0.5 : 1,
                          }}
                        >
                          <FiSend size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={generarConfigIA}
                        disabled={generandoConfig}
                        style={{
                          ...S.nextBtn,
                          width: '100%',
                          justifyContent: 'center',
                          background: generandoConfig
                            ? COL.muted
                            : `linear-gradient(135deg,${COL.green},#059669)`,
                        }}
                      >
                        {generandoConfig ? (
                          <>
                            <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} />{' '}
                            Generando...
                          </>
                        ) : (
                          <>
                            Generar configuración del bot <FiCpu size={16} />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {iaFase === 'preview' && iaConfig && (
                  <div>
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Prompt del sistema</div>
                      <textarea
                        rows={4}
                        value={iaConfig.prompt_sistema || ''}
                        onChange={(e) =>
                          setIaConfig({ ...iaConfig, prompt_sistema: e.target.value })
                        }
                        style={{
                          ...S.input,
                          background: COL.bg,
                          border: `1px solid ${COL.border}`,
                          borderRadius: '10px',
                          padding: '10px',
                          fontSize: '13px',
                          resize: 'vertical',
                          width: '100%',
                          lineHeight: 1.5,
                        }}
                      />
                    </div>
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Conocimiento extra</div>
                      <textarea
                        rows={2}
                        value={iaConfig.conocimiento_extra || ''}
                        onChange={(e) =>
                          setIaConfig({ ...iaConfig, conocimiento_extra: e.target.value })
                        }
                        style={{
                          ...S.input,
                          background: COL.bg,
                          border: `1px solid ${COL.border}`,
                          borderRadius: '10px',
                          padding: '10px',
                          fontSize: '13px',
                          resize: 'vertical',
                          width: '100%',
                          lineHeight: 1.5,
                        }}
                      />
                    </div>
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Fases de venta</div>
                      {['bienvenida', 'descubrimiento', 'negociacion', 'cierre', 'postventa'].map(
                        (f) => (
                          <div key={f} style={{ marginBottom: '10px' }}>
                            <label
                              style={{ ...S.label, fontSize: '12px', textTransform: 'capitalize' }}
                            >
                              {f}
                            </label>
                            <textarea
                              rows={2}
                              value={iaConfig[`fase_${f}`] || ''}
                              onChange={(e) =>
                                setIaConfig({ ...iaConfig, [`fase_${f}`]: e.target.value })
                              }
                              style={{
                                ...S.input,
                                background: COL.bg,
                                border: `1px solid ${COL.border}`,
                                borderRadius: '8px',
                                padding: '8px 10px',
                                fontSize: '12px',
                                resize: 'vertical',
                                width: '100%',
                                lineHeight: 1.5,
                              }}
                            />
                          </div>
                        )
                      )}
                    </div>
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Politicas y detalles</div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
                          gap: '10px',
                        }}
                      >
                        {[
                          { k: 'horario_semana', l: 'Horario semana' },
                          { k: 'horario_finde', l: 'Horario finde' },
                          { k: 'metodos_pago', l: 'Métodos de pago' },
                          { k: 'zonas_cobertura', l: 'Zonas cobertura' },
                        ].map((p) => (
                          <div key={p.k}>
                            <label style={{ ...S.label, fontSize: '12px' }}>{p.l}</label>
                            <input
                              value={iaConfig[p.k] || ''}
                              onChange={(e) => setIaConfig({ ...iaConfig, [p.k]: e.target.value })}
                              style={{
                                ...S.input,
                                background: COL.bg,
                                border: `1px solid ${COL.border}`,
                                borderRadius: '8px',
                                padding: '8px 10px',
                                fontSize: '12px',
                                width: '100%',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      {[
                        { k: 'politicas_devolucion', l: 'Políticas de devolución' },
                        { k: 'notas_negocio', l: 'Notas del negocio' },
                      ].map((p) => (
                        <div key={p.k} style={{ marginTop: '10px' }}>
                          <label style={{ ...S.label, fontSize: '12px' }}>{p.l}</label>
                          <textarea
                            rows={2}
                            value={iaConfig[p.k] || ''}
                            onChange={(e) => setIaConfig({ ...iaConfig, [p.k]: e.target.value })}
                            style={{
                              ...S.input,
                              background: COL.bg,
                              border: `1px solid ${COL.border}`,
                              borderRadius: '8px',
                              padding: '8px 10px',
                              fontSize: '12px',
                              resize: 'vertical',
                              width: '100%',
                              lineHeight: 1.5,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        setIaFase('chat');
                        setIaListo(false);
                        setIaConfig(null);
                      }}
                      style={{ ...S.backBtn, fontSize: '12px', padding: '6px 14px' }}
                    >
                      Reiniciar chat IA
                    </button>
                  </div>
                )}
              </div>
            )}

            {errorGeneral && (
              <div
                style={{
                  background: COL.redBg,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginTop: '16px',
                  fontSize: '13px',
                  color: COL.red,
                  fontWeight: 500,
                }}
              >
                {errorGeneral}
              </div>
            )}

            <div style={S.navBar}>
              <button onClick={anterior} style={S.backBtn}>
                <FiArrowLeft size={16} /> {paso === 1 ? 'Inicio' : 'Anterior'}
              </button>
              {paso < 6 ? (
                <button onClick={siguiente} style={S.nextBtn}>
                  Siguiente <FiArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={enviarRegistro}
                  disabled={enviando}
                  style={{
                    ...S.nextBtn,
                    background: enviando
                      ? COL.muted
                      : `linear-gradient(135deg,${COL.green},#059669)`,
                    boxShadow: enviando ? 'none' : `0 4px 14px ${COL.green}30`,
                  }}
                >
                  {enviando ? (
                    <>
                      <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} />{' '}
                      Registrando...
                    </>
                  ) : (
                    <>
                      Registrar Empresa <FiCheck size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <div style={S.footer}>
            <p style={{ fontSize: '13px', color: COL.muted, margin: 0 }}>
              Ya tienes cuenta?{' '}
              <span
                onClick={() => nav('/')}
                style={{ color: COL.acc, cursor: 'pointer', fontWeight: 600 }}
              >
                Volver al inicio
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(160deg, #0a0e27 0%, #111b47 30%, #14205e 60%, #1a2f7a 100%)`,
    fontFamily: "'Inter','Segoe UI',system-ui,-apple-system,sans-serif",
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: 'rgba(10,14,39,0.85)',
    backdropFilter: 'blur(14px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0 20px',
  },
  container: { maxWidth: '600px', margin: '0 auto', padding: '0 16px' },
  cancelBtn: {
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  body: { paddingTop: '80px', paddingBottom: '40px', minHeight: '100vh' },
  card: {
    background: COL.white,
    borderRadius: '20px',
    padding: 'clamp(24px,4vw,36px)',
    boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
  },
  title: {
    fontSize: 'clamp(22px,3.5vw,28px)',
    fontWeight: 800,
    color: COL.pri,
    textAlign: 'center',
    margin: '0 0 4px',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: COL.muted,
    textAlign: 'center',
    margin: '0 0 28px',
    lineHeight: 1.5,
  },
  stepper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    marginBottom: '32px',
  },
  stepLine: { flex: '0 1 40px', height: '2px', borderRadius: '1px', transition: 'background 0.3s' },
  stepContent: { minHeight: '280px' },
  stepTitle: {
    fontSize: 'clamp(18px,2.5vw,22px)',
    fontWeight: 700,
    color: COL.pri,
    margin: '0 0 4px',
  },
  stepDesc: { fontSize: '14px', color: COL.muted, margin: '0 0 24px', lineHeight: 1.5 },
  fieldGroup: { marginBottom: '18px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: COL.text,
    marginBottom: '6px',
  },
  inputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: COL.bg,
    borderRadius: '12px',
    padding: '12px 14px',
    border: `1px solid ${COL.border}`,
    transition: 'border-color 0.2s',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '15px',
    fontFamily: 'inherit',
    color: COL.pri,
  },
  select: {
    background: COL.bg,
    border: `1px solid ${COL.border}`,
    borderRadius: '12px',
    padding: '12px 10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: COL.text,
    cursor: 'pointer',
    outline: 'none',
    minWidth: '100px',
  },
  tipoGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  tipoCard: {
    borderRadius: '12px',
    padding: '14px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tipoCardHeader: { display: 'flex', alignItems: 'center', gap: '10px' },
  tipoRadio: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  tipoRadioDot: { width: '8px', height: '8px', borderRadius: '50%', background: COL.white },
  eyeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
  },
  valIndicator: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  spinner: {
    width: '14px',
    height: '14px',
    border: `2px solid ${COL.border}`,
    borderTopColor: COL.acc,
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  errorText: {
    display: 'block',
    fontSize: '12px',
    color: COL.red,
    marginTop: '4px',
    fontWeight: 500,
  },
  hintText: {
    display: 'block',
    fontSize: '12px',
    color: COL.green,
    marginTop: '4px',
    fontWeight: 500,
  },
  navBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '28px',
    paddingTop: '20px',
    borderTop: `1px solid ${COL.border}`,
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: `1px solid ${COL.border}`,
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: COL.muted,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  nextBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: `linear-gradient(135deg,${COL.pri2},${COL.acc})`,
    border: 'none',
    borderRadius: '10px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 700,
    color: COL.white,
    cursor: 'pointer',
    boxShadow: `0 4px 14px ${COL.acc}30`,
    transition: 'all 0.2s',
  },
  footer: { textAlign: 'center', marginTop: '24px' },
  section: { marginBottom: '24px', paddingBottom: '20px', borderBottom: `1px solid ${COL.border}` },
  sectionTitle: { fontSize: '14px', fontWeight: 700, color: COL.pri, marginBottom: '10px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  optionCard: { borderRadius: '12px', padding: '14px', transition: 'all 0.2s' },
  resRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${COL.border}`,
  },
  resLabel: { fontSize: '13px', color: COL.muted },
  resValue: { fontSize: '14px', fontWeight: 600, color: COL.text },
  helpText: { fontSize: '12px', color: COL.muted, margin: '0 0 12px', lineHeight: 1.5 },
  logoRow: { display: 'flex', alignItems: 'center', gap: '14px' },
  logoPreview: {
    width: '72px',
    height: '72px',
    borderRadius: '14px',
    background: COL.bg,
    border: `1px solid ${COL.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  logoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  logoBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: COL.bg,
    border: `1px solid ${COL.border}`,
    borderRadius: '10px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: COL.pri2,
    cursor: 'pointer',
  },
  logoRemove: {
    background: 'none',
    border: 'none',
    color: COL.red,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: '10px',
    padding: '8px 4px',
  },
  planGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' },
  planCard: {
    border: `1px solid ${COL.border}`,
    borderRadius: '14px',
    padding: '16px',
    cursor: 'pointer',
    background: COL.white,
    transition: 'all 0.2s',
  },
  planCardActive: {
    border: `2px solid ${COL.acc}`,
    background: `${COL.acc}08`,
    boxShadow: `0 4px 14px ${COL.acc}20`,
  },
  planFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12.5px',
    color: COL.text,
    padding: '3px 0',
    lineHeight: 1.4,
  },
  notaBox: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    background: COL.amberBg,
    border: `1px solid ${COL.amber}30`,
    borderRadius: '12px',
    padding: '12px 14px',
    fontSize: '13px',
    color: '#92400e',
    lineHeight: 1.5,
  },
};

export default RegistroEmpresa;
